import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Empty,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { SaleResult } from '@never2busy/shared';
import { salesApi, type Sale } from '../../../api/endpoints/sales';
import { lookupsApi } from '../../../api/endpoints/lookups';
import { PageHeader } from '../../../components/common/PageHeader';

const { Text } = Typography;
const NO_STAGE_KEY = '__no_stage';

interface DragPayload {
  saleId: number;
  fromStage: string;
}

export function SalesPipelinePage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [overColumn, setOverColumn] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sales', 'pipeline'],
    queryFn: () => salesApi.pipeline(),
    refetchInterval: 30_000,
  });

  const { data: stageLookups } = useQuery({
    queryKey: ['lookups', 'sale_stage'],
    queryFn: () => lookupsApi.byList('sale_stage'),
    staleTime: 5 * 60_000,
  });

  const stages = useMemo(
    () => (stageLookups ?? []).map((l) => ({ value: l.value, label: l.label })),
    [stageLookups],
  );

  const byStage = useMemo(() => {
    const groups = new Map<string, Sale[]>();
    stages.forEach((s) => groups.set(s.value, []));
    groups.set(NO_STAGE_KEY, []);
    (data ?? []).forEach((sale) => {
      const key = sale.stage && groups.has(sale.stage) ? sale.stage : NO_STAGE_KEY;
      groups.get(key)?.push(sale);
    });
    return groups;
  }, [data, stages]);

  const setStageMutation = useMutation({
    mutationFn: ({ id, stage }: { id: number; stage: string | null }) =>
      salesApi.setStage(id, stage),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
    },
    onError: () => message.error('No se pudo mover la venta'),
  });

  const setResultMutation = useMutation({
    mutationFn: ({ id, result }: { id: number; result: SaleResult }) =>
      salesApi.setResult(id, result),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => message.error('No se pudo actualizar'),
  });

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    payload: DragPayload,
  ): void => {
    e.dataTransfer.setData('application/json', JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, columnKey: string): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overColumn !== columnKey) setOverColumn(columnKey);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetStageKey: string,
  ): void => {
    e.preventDefault();
    setOverColumn(null);
    try {
      const payload = JSON.parse(e.dataTransfer.getData('application/json')) as DragPayload;
      if (payload.fromStage === targetStageKey) return;
      const newStage = targetStageKey === NO_STAGE_KEY ? null : targetStageKey;
      setStageMutation.mutate({ id: payload.saleId, stage: newStage });
    } catch {
      /* drop invalido */
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const columns: Array<{ key: string; label: string }> = [
    ...stages.map((s) => ({ key: s.value, label: s.label })),
    { key: NO_STAGE_KEY, label: 'Sin etapa' },
  ];

  return (
    <>
      <PageHeader
        title="Pipeline de ventas"
        crumbs={[{ title: 'Ventas', to: '/ventas' }, { title: 'Pipeline' }]}
        extra={
          <Space>
            <Button icon={<UnorderedListOutlined />} onClick={() => navigate('/ventas')}>
              Ver lista
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/ventas/nueva')}
            >
              Nueva venta
            </Button>
          </Space>
        }
      />

      <div
        style={{
          display: 'flex',
          gap: 12,
          overflowX: 'auto',
          paddingBottom: 8,
        }}
      >
        {columns.map((col) => {
          const items = byStage.get(col.key) ?? [];
          const total = items.reduce(
            (sum, s) => sum + (s.amount ? parseFloat(s.amount) : 0),
            0,
          );
          const isOver = overColumn === col.key;
          return (
            <div
              key={col.key}
              onDragOver={(e) => handleDragOver(e, col.key)}
              onDragLeave={() => setOverColumn((c) => (c === col.key ? null : c))}
              onDrop={(e) => handleDrop(e, col.key)}
              style={{
                minWidth: 280,
                width: 280,
                background: isOver ? '#e6f4ff' : '#f5f5f5',
                borderRadius: 8,
                padding: 12,
                border: isOver ? '2px dashed #1677ff' : '2px dashed transparent',
                transition: 'background 0.15s, border-color 0.15s',
              }}
            >
              <Space
                style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}
              >
                <Text strong>
                  {col.label}{' '}
                  <Text type="secondary" style={{ fontWeight: 400 }}>
                    ({items.length})
                  </Text>
                </Text>
                {total > 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {total.toLocaleString('es-MX', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </Text>
                )}
              </Space>

              {items.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  imageStyle={{ height: 40 }}
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Suelta aquí
                    </Text>
                  }
                />
              ) : (
                items.map((sale) => (
                  <Card
                    key={sale.id}
                    size="small"
                    draggable
                    onDragStart={(e) =>
                      handleDragStart(e, { saleId: sale.id, fromStage: col.key })
                    }
                    style={{ marginBottom: 8, cursor: 'grab' }}
                    bodyStyle={{ padding: 12 }}
                  >
                    <Link to={`/ventas/${sale.id}`} style={{ fontWeight: 500 }}>
                      {sale.name}
                    </Link>
                    {sale.account && (
                      <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                        {sale.account.name}
                      </div>
                    )}
                    <Space size={6} wrap style={{ marginTop: 8 }}>
                      {sale.amount && (
                        <Tag color="blue">
                          {parseFloat(sale.amount).toLocaleString('es-MX')}{' '}
                          {sale.currency?.code ?? ''}
                        </Tag>
                      )}
                      {sale.probability != null && <Tag>{sale.probability}%</Tag>}
                    </Space>
                    {sale.assignedTo && (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                        {sale.assignedTo.firstName} {sale.assignedTo.lastName}
                      </div>
                    )}
                    <Space size={4} style={{ marginTop: 8 }}>
                      <Tooltip title="Marcar como ganada">
                        <Button
                          type="text"
                          size="small"
                          icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                          onClick={() =>
                            setResultMutation.mutate({ id: sale.id, result: SaleResult.WON })
                          }
                        />
                      </Tooltip>
                      <Tooltip title="Marcar como perdida">
                        <Button
                          type="text"
                          size="small"
                          icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                          onClick={() =>
                            setResultMutation.mutate({ id: sale.id, result: SaleResult.LOST })
                          }
                        />
                      </Tooltip>
                    </Space>
                  </Card>
                ))
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
